import { token } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Descriptions, Divider, Flex } from 'antd';
import styled from 'styled-components';
import {
  Main,
  ObjectHeader,
  Padding,
  SidePanel,
} from '../../common/components';
import { TextBadge } from '../../common/components/TextBadge';
import { getDatePeriodString } from '../../common/utils';
import { AlternativeAlgorithmSetDataType } from '../schema';
import translations from '../translations/AlternativeAlgorithmViewPanel.translations.json';
import { AlternativeButton } from './AlternativeButton';
import { ViewableGroupOrAlgorithm } from './types';

const { addTranslations, useTranslation } = i18nNsFactory(
  'alternatives-details-view',
);
addTranslations(translations);

export interface AlternativeAlgorithmViewPanelProps {
  details: AlternativeAlgorithmSetDataType;
  onClose: () => void;
  onSelectGroupOrAlgorithm: (viewable: ViewableGroupOrAlgorithm) => void;
}

export function AlternativeAlgorithmViewPanel({
  details,
  onClose,
  onSelectGroupOrAlgorithm,
}: AlternativeAlgorithmViewPanelProps) {
  const { t } = useTranslation();

  return (
    <SidePanel
      minWidth={700}
      side="right"
      collapsedWidth={700}
      width={1786}
      mode="layered"
    >
      <SidePanelWrapper>
        <Padding>
          <Flex align="end" justify="space-between">
            <ObjectHeader
              upperText={t('alternative.code.label', {
                code: details.code,
              })}
              lowerText={details.name}
            />
          </Flex>
        </Padding>
        <Divider style={{ margin: 0 }} />
        <Scrollable>
          <ScrollContent>
            <Descriptions
              column={1}
              layout="horizontal"
              items={[
                {
                  key: 'alternatives',
                  label: t('fields.alternatives.label'),
                  children: (
                    <Flex vertical>
                      {details.alternativeAlgorithms.map((alternative) => {
                        const groupCode = alternative.groupCode;
                        const code = alternative.code;
                        const label = groupCode ?? code ?? '';

                        const btn = (
                          <AlternativeButton
                            id={`alternative-${label}`}
                            type="link"
                            onClick={getAlternativeClickHandler(
                              details.code,
                              onSelectGroupOrAlgorithm,
                              groupCode,
                              code,
                            )}
                          >
                            {groupCode ?? code ?? ''}
                          </AlternativeButton>
                        );

                        if (alternative.defaultAlgorithm) {
                          return <TextBadge key={label}>{btn}</TextBadge>;
                        }

                        return <span key={label}>{btn}</span>;
                      })}
                    </Flex>
                  ),
                },
                {
                  key: 'exceptions',
                  label: t('fields.exceptions.label'),
                  children: (
                    <Flex vertical>
                      {details.alternativeAlgorithms.flatMap(
                        (alternative, alternativeIndex) => {
                          const groupCode = alternative.groupCode;
                          const code = alternative.code;

                          return alternative.exceptions.map(
                            (exception, index) => (
                              <div key={`exc-${alternativeIndex}-${index}`}>
                                {getDatePeriodString(
                                  exception.validPeriod[0],
                                  exception.validPeriod[1],
                                )}
                                {' - '}
                                <AlternativeButton
                                  id={`exc-alternative-${index}`}
                                  type="link"
                                  onClick={getAlternativeClickHandler(
                                    details.code,
                                    onSelectGroupOrAlgorithm,
                                    groupCode,
                                    code,
                                  )}
                                >
                                  {groupCode ?? code ?? ''}
                                </AlternativeButton>
                              </div>
                            ),
                          );
                        },
                      )}
                    </Flex>
                  ),
                },
              ]}
            />
          </ScrollContent>
        </Scrollable>
        <Main.Footer>
          <FullWidthFlex justify="end" gap="small">
            <Button
              id="close-alternatives-view"
              onClick={onClose}
              type="primary"
            >
              {t('button.close')}
            </Button>
          </FullWidthFlex>
        </Main.Footer>
      </SidePanelWrapper>
    </SidePanel>
  );
}

export const getAlternativeClickHandler = (
  alternativeCode: number,
  onSelectGroupOrAlgorithm: (viewable: ViewableGroupOrAlgorithm) => void,
  groupCode?: string | null,
  code?: string | null,
) => {
  let onClick: (() => void) | undefined;
  if (groupCode) {
    onClick = () =>
      onSelectGroupOrAlgorithm({
        groupCode,
        alternativeCode,
      });
  } else if (code) {
    onClick = () =>
      onSelectGroupOrAlgorithm({
        code,
        alternativeCode,
      });
  }

  return onClick;
};

const FullWidthFlex = styled(Flex)`
  width: 100%;
`;
const Scrollable = styled.div`
  overflow-y: auto;
`;
const ScrollContent = styled(Padding)`
  overflow-x: hidden;
`;
const SidePanelWrapper = styled.div`
  display: grid;
  grid-template-rows: auto auto 1fr 60px;
  height: 100%;
  width: 100%;
  overflow-y: hidden;
  border-left: 1px solid ${token.colorBorderSecondary};
`;
